import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface RoutineType {
    id: number;
    description: string;
    initialDate: string;
    finalDate: string;
    numberOfCharges: number;
    frequency: string;
    billingFrequency: number;
    razaoSocial: string;
    type: string;
    routineTypeId: number;
    emails: string[];
    tags: string[];
}

export interface CreateRoutineType {
    description: string;
    initialDate: string;
    finalDate: string | null;
    numberOfCharges: number | null;
    frequency: number;
    billingFrequency: number;
    officeId: number;
    customerId: number;
    routineTypeId: number;
    emails: string[];
    tags: string[];
    onlyBusinessDays: boolean;
    businessDayOfMonth: number | null;
}

export interface UpdateRoutineType {
    id: number;
    description: string;
    routineTypeId: number;
    emails: string[];
    tags: string[];
}

export default function useRoutine() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listRoutine, setListRoutine] = useState<RoutineType[]>([]);
    const [pageCount, setPageCount] = useState<number>(0);
    const [initialListRoutine, setInitialListRoutine] = useState<RoutineType[]>([]);
    const [routine, setRoutine] = useState<RoutineType>({
        id: 0,
        description: "",
        initialDate: "",
        finalDate: "",
        numberOfCharges: 0,
        frequency: "",
        billingFrequency: 0,
        razaoSocial: "",
        type: "",
        routineTypeId: 0,
        emails: [],
        tags: []
    });

    function createRoutine(createRoutineType: CreateRoutineType): Promise<CreateRoutineType | null> {
        setLoading(true);
        return api
            .post(`/Routine/create`, createRoutineType)
            .then((response) => {
                const createdRoutine: CreateRoutineType = response.data;
                showToast("success", "Rotina criada com sucesso.");
                setLoading(false);
                return createdRoutine;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor fazer login novamente.", error);
                } else {
                    showToast("error", "Erro ao criar rotina. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao criar rotina:", error);
                return null;
            });
    }

    async function importRoutine(file: File, officeId: number): Promise<string | null> {
        setLoading(true);

        const formData = new FormData();
        formData.append("file", file);

        // return api
        //     .post(`/Routine/import?officeId=${officeId}`, formData, {
        //         headers: {
        //             "Content-Type": "multipart/form-data",
        //         },
        //     })
        //     .then((response) => {
        //         showToast("success", `Rotinas importadas com sucesso. Total: ${response.data.count}`);
        //         setLoading(false);
        //         return response.data.message;
        //     })
        //     .catch((error) => {
        //         if (error?.response?.status === 401) {
        //             handleLogout();
        //             showToast(
        //                 "error",
        //                 "Sessão desconectada. Favor login novamente.",
        //                 error
        //             );
        //         } else {
        //             showToast(
        //                 "error",
        //                 "Erro ao importar rotinas. Verifique novamente mais tarde.",
        //                 error
        //             );
        //         }
        //         setLoading(false);
        //         console.error("Erro ao importar rotinas:", error);
        //         return null;
        //     });
        try {
            const response = await api.post(`/Routine/import?officeId=${officeId}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            showToast("success", `Rotinas importadas com sucesso. Total: ${response.data.count}`);
            return response.data.message;
        } catch (error: any) {
            if (error?.response?.status === 401) {
                handleLogout();
                showToast("error", "Sessão desconectada. Favor login novamente.", error);
            } else {
                const backendMsg = await getBackendMessage(error);
                showToast("error", backendMsg || "Erro ao importar rotinas. Verifique novamente mais tarde.", error);
            }
            console.error("Erro ao importar rotinas:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    function getAllRoutinePaged(
        pageNumber: number,
        officeId?: number,
        customerId?: number,
        userCustomerId?: number,
        description?: string,
        initialDate?: string,
        finalDate?: string,
        frequency?: string,
    ): Promise<RoutineType[] | null> {
        setLoading(true);
        let url = `/Routine/getAllRoutinePaged?pageNumber=${pageNumber}&pageSize=10`;

        if (description) url += `&description=${description}`;
        if (officeId) url += `&officeId=${officeId}&routineId=0`;
        if (customerId) url += `&customerId=${customerId}`;
        if (userCustomerId) url += `&userCustomerId=${userCustomerId}`;
        if (initialDate && finalDate) url += `&initialDate=${initialDate}&finalDate=${finalDate}`;
        if (frequency) url += `&frequency=${frequency}`;

        return api
            .get(url)
            .then((response) => {
                setListRoutine(response.data.result);
                setInitialListRoutine(response.data.result);
                setPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor fazer login novamente.", error);
                } else {
                    showToast("error", "Erro ao carregar rotinas. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao carregar rotinas:", error);
                return null;
            });
    }

    function updateRoutine(updateRoutineData: UpdateRoutineType): Promise<UpdateRoutineType | null> {
        setLoading(true);
        return api
            .put(`/Routine/${updateRoutineData.id}`, updateRoutineData)
            .then((response) => {
                showToast("success", "Rotina atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor fazer login novamente.", error);
                } else {
                    showToast("error", "Erro ao atualizar rotina. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao atualizar rotina:", error);
                return null;
            });
    }

    function deleteRoutine(routineId: number): Promise<RoutineType | null> {
        setLoading(true);
        return api
            .delete(`/Routine/${routineId}`)
            .then(() => {
                setRoutine({
                    id: 0,
                    description: "",
                    initialDate: "",
                    finalDate: "",
                    numberOfCharges: 0,
                    frequency: "",
                    billingFrequency: 0,
                    razaoSocial: "",
                    type: "",
                    routineTypeId: 0,
                    emails: [],
                    tags: []
                });
                showToast("success", "Rotina deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor fazer login novamente.", error);
                } else {
                    showToast("error", "Erro ao deletar rotina. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao deletar rotina:", error);
                return null;
            });
    }

    async function getBackendMessage(error: any): Promise<string | null> {
        try {
            const data = error?.response?.data;

            if (data instanceof Blob) {
                const text = await data.text();
                return text?.trim() || null;
            }

            if (typeof data === "string") return data.trim();

            if (typeof data === "object" && data) {
                if (typeof data.message === "string" && data.message) return data.message.trim();
                if (typeof data.detail === "string" && data.detail) return data.detail.trim();

                if (data.errors && typeof data.errors === "object") {
                    const parts: string[] = [];
                    for (const k of Object.keys(data.errors)) {
                        const arr = data.errors[k];
                        if (Array.isArray(arr) && arr.length) parts.push(`${k}: ${arr.join(", ")}`);
                    }
                    if (parts.length) return parts.join(" | ");
                }
            }
        } catch {
        }
        return error?.message || null;
    }


    return {
        setInitialListRoutine,
        initialListRoutine,
        setListRoutine,
        listRoutine,
        setRoutine,
        routine,
        setPageCount,
        pageCount,
        createRoutine,
        importRoutine,
        getAllRoutinePaged,
        updateRoutine,
        deleteRoutine,
    };
}
